import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Check, RefreshCw } from 'lucide-react';
import { apiService } from '@/services/api';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';

/**
 * ReviewPage — the public "How was your visit?" page a guest opens from the owner's link
 * (`/review/:refType/:refId`, refType = booking | appointment). Tap ★ 1–5, add an optional comment,
 * submit once. No login.
 */
type Phase = 'loading' | 'error' | 'form' | 'done';

const ReviewPage: React.FC = () => {
  const { refType = '', refId = '' } = useParams<{ refType: string; refId: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [branchName, setBranchName] = useState('');
  const [context, setContext] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiService.getPublicReview(refType, refId);
        if (!alive) return;
        const d = res?.data || {};
        setBranchName(d.branchName || 'the venue');
        setContext(d.context || '');
        if (d.alreadyReviewed) { setRating(d.rating || 0); setPhase('done'); }
        else setPhase('form');
      } catch (e) {
        if (!alive) return;
        setPhase('error'); setMessage(e instanceof Error ? e.message : 'Something went wrong.');
      }
    })();
    return () => { alive = false; };
  }, [refType, refId]);

  const submit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true); setMessage('');
    try {
      await apiService.submitPublicReview(refType, refId, { rating, comment: comment.trim() || undefined, name: name.trim() || undefined });
      setPhase('done');
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not submit. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const shown = hover || rating;
  return (
    <div style={s.page}>
      <ErrorBoundary>
        <div style={s.card}>
          {phase === 'loading' ? (
            <div style={s.center}><div style={s.muted}>Loading…</div></div>
          ) : phase === 'error' ? (
            <div style={s.center}><div style={s.iconBad}><RefreshCw size={26} color="#b91c1c" /></div><div style={s.h2}>Can’t open this link</div><div style={s.muted}>{message}</div></div>
          ) : phase === 'done' ? (
            <div style={s.center}>
              <div style={s.iconGood}><Check size={40} color="#fff" strokeWidth={3} /></div>
              <div style={s.h1}>Thank you!</div>
              <div style={s.muted}>Your feedback for {branchName} has been received{rating ? ` (${rating}★)` : ''}.</div>
            </div>
          ) : (
            <>
              <div style={s.brandMark}><Star size={26} color="#fff" fill="#fff" /></div>
              <div style={s.h1}>How was your visit?</div>
              <div style={s.sub}>{branchName}{context ? ` · ${context}` : ''}</div>

              <div style={s.stars}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" style={s.starBtn} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}>
                    <Star size={40} color={n <= shown ? '#f59e0b' : '#d1d5db'} fill={n <= shown ? '#f59e0b' : 'none'} />
                  </button>
                ))}
              </div>
              <div style={s.ratingLabel}>{['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][shown] || 'Tap a star'}</div>

              <textarea style={s.input} rows={3} maxLength={1000} placeholder="Tell them what you loved (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <input style={s.input} maxLength={100} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />

              {message && <div style={s.err}>{message}</div>}
              <button style={{ ...s.submit, opacity: rating && !submitting ? 1 : 0.5 }} disabled={!rating || submitting} onClick={submit}>
                {submitting ? 'Sending…' : 'Submit review'}
              </button>
            </>
          )}
        </div>
      </ErrorBoundary>
      <style>{`@keyframes cuPop{0%{transform:scale(0.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: '100dvh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" },
  card: { width: 'min(440px, 100%)', background: '#fff', borderRadius: 20, padding: '28px 22px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
  brandMark: { width: 60, height: 60, borderRadius: 18, background: 'linear-gradient(135deg,#f59e0b,#f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', boxShadow: '0 10px 26px rgba(245,158,11,0.4)' },
  h1: { fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' },
  h2: { fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginTop: 6 },
  sub: { color: '#64748b', fontSize: '0.95rem', marginTop: 4, marginBottom: 6 },
  stars: { display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 },
  starBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 },
  ratingLabel: { fontWeight: 700, color: '#b45309', minHeight: 22, marginTop: 6, marginBottom: 8 },
  input: { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '11px 13px', fontSize: '0.95rem', color: '#0f172a', outline: 'none', marginTop: 10, fontFamily: 'inherit', resize: 'vertical' },
  submit: { width: '100%', marginTop: 14, background: 'linear-gradient(135deg,#f59e0b,#f97316)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 10px 24px rgba(245,158,11,0.34)' },
  err: { background: '#fef2f2', color: '#b91c1c', borderRadius: 10, padding: '8px 12px', fontSize: '0.85rem', marginTop: 10 },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 6px' },
  muted: { color: '#64748b', fontSize: '0.95rem', lineHeight: 1.5 },
  iconGood: { width: 84, height: 84, borderRadius: '50%', background: 'linear-gradient(135deg,#22c55e,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 34px rgba(34,197,94,0.34)', animation: 'cuPop 0.5s ease-out' },
  iconBad: { width: 60, height: 60, borderRadius: '50%', background: 'rgba(185,28,28,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};

export default ReviewPage;
